import beeIcon from "@/assets/images/decor/BeeIcon.svg";
import grass2Icon from "@/assets/images/decor/Grass2Icon.svg";
import pinkFlowerIcon from "@/assets/images/decor/PinkFlowerIcon.svg";
import purpleFlowerIcon from "@/assets/images/decor/PurpleFlowerIcon.svg";
import yellowFlowerIcon from "@/assets/images/decor/YellowFlowerIcon.svg";
import { LiveMobileViewContainer } from "@/components/slides/editor/LiveMobileViewContainer";
import { GetGrammarResponseDocument } from "@/generated/graphql";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { useAppSelector } from "@/store/hooks";
import { deriveOpeningLine } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { BiText } from "react-icons/bi";
import { HiChatAlt2, HiLightBulb, HiVolumeUp, HiXCircle } from "react-icons/hi";
import { HiDocumentText } from "react-icons/hi2";
import { IoPause, IoSend, IoStop, IoVolumeHigh } from "react-icons/io5";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

const toBubbles = (reply: string, tag: string): ChatMessage[] =>
  reply.split("|||").map((content, i) => ({
    id: `${tag}-${i}`,
    role: "assistant" as const,
    content: content.trim(),
  }));

// The string-only preview has no shouldTerminate flag — infer the end from the farewell line.
const isFarewell = (bubbles: ChatMessage[]) =>
  /\b(see you|goodbye|bye)\b/i.test(bubbles.at(-1)?.content ?? "");

// The web preview has no scoring pipeline — this is a sample layout so the teacher sees the card.
const pctColor = (p: number) => (p >= 85 ? "#56AC60" : p >= 60 ? "#FCD327" : "#E5434A");
const errColor = (n: number) => (n < 3 ? "#56AC60" : n < 5 ? "#FCD327" : "#E5434A");
const SAMPLE_SCORES = [
  { label: "Word Choice", icon: BiText, value: "80%", bg: pctColor(80) },
  { label: "Understanding", icon: HiLightBulb, value: "75%", bg: pctColor(75) },
  { label: "Errors", icon: HiXCircle, value: "2", bg: errColor(2) },
  { label: "Communication", icon: HiChatAlt2, value: "85%", bg: pctColor(85) },
  { label: "Grammar", icon: HiDocumentText, value: "70%", bg: pctColor(70) },
];

export function GrammarFormLiveMobileView() {
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [ended, setEnded] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const openedForRef = useRef<string | null>(null);

  const [getGrammarResponse, { loading }] = useMutation(GetGrammarResponseDocument);

  const messageCount = messages.length;
  useEffect(() => {
    if (messageCount >= 0) endRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messageCount]);

  // Turn 0 mirrors mobile: one opening line from grammarFocus, no model call until the teacher replies.
  useEffect(() => {
    if (!slide || openedForRef.current === slide.id) return;
    openedForRef.current = slide.id;
    setInput("");
    setEnded(false);
    setMessages([
      { id: "open", role: "assistant", content: deriveOpeningLine(slide.limitation?.grammarFocus) },
    ]);
  }, [slide]);

  if (!slide) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || loading || ended) return;

    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: "user", content: text };
    const history = messages;
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    try {
      const res = await getGrammarResponse({
        variables: {
          message: text,
          limitation: slide.limitation,
          history,
          slideId: slide.id,
        },
      });
      const reply = res.data?.getGrammarResponse ?? "Please try again …";
      const bubbles = toBubbles(reply, `${Date.now()}`);
      setMessages((prev) => [...prev, ...bubbles]);
      if (isFarewell(bubbles)) setEnded(true);
    } catch (error) {
      // The bubble can only ever say "try again", so a spent balance needs its own toast.
      toastAiCredits(error, "grammarPreviewToast");
      setMessages((prev) => [
        ...prev,
        { id: `${Date.now()}-err`, role: "assistant", content: "Please try again …" },
      ]);
    }
  };

  return (
    <LiveMobileViewContainer>
      {/* Top-right controls — matches Grammar.tsx's topControlsRow: mic-pause + end. Icon is a
          pause on the AI's turn, a speaker on yours. */}
      <div className="absolute right-10 top-20 z-20 flex gap-3">
        <div
          className="pointer-events-none flex h-10 w-10 items-center justify-center rounded-full shadow-md"
          style={{ background: "linear-gradient(180deg, #FF3612 0%, #FF992A 100%)" }}
        >
          {loading ? (
            <IoPause className="text-white text-base" />
          ) : (
            <IoVolumeHigh className="text-white text-base" />
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowEndConfirm(true)}
          className="flex h-10 w-10 items-center justify-center rounded-full shadow-md"
          style={{ background: "linear-gradient(180deg, #FF3612 0%, #FF992A 100%)" }}
        >
          <IoStop className="text-white text-base" />
        </button>
      </div>

      {/* Garden — decoration, matches Grammar.tsx's bottom flower row. Sits just above the input
          bar (a horizon strip) rather than behind it, where it would be hidden. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-16 flex items-end justify-around px-[5%]"
        style={{ zIndex: 0 }}
      >
        <img src={beeIcon} alt="" className="absolute left-[16%] bottom-[55%] w-[8%]" />
        <img src={purpleFlowerIcon} alt="" className="w-[10%]" />
        <img src={grass2Icon} alt="" className="w-[20%]" />
        <img src={yellowFlowerIcon} alt="" className="w-[15%]" />
        <img src={pinkFlowerIcon} alt="" className="w-[15%]" />
      </div>

      {/* Conversation — top padding clears the floating control buttons. */}
      <div className="relative z-10 flex-1 w-full min-h-0 overflow-y-auto flex flex-col gap-3 px-3 pt-14">
        {messages.map((m) =>
          m.role === "assistant" ? (
            <div key={m.id} className="flex items-center gap-2">
              <div
                className="flex items-center gap-1 bg-white border rounded-xl pl-2 pr-3 py-1.5 max-w-[75%]"
                style={{ borderColor: "#DDDDDD" }}
              >
                <HiVolumeUp style={{ color: "#DFDFDF" }} className="shrink-0" fontSize={24} />
                <span className="text-sm font-bold text-gray-800">{m.content}</span>
              </div>
            </div>
          ) : (
            <div key={m.id} className="flex items-center gap-2 justify-end">
              <div
                className="flex items-center gap-1 rounded-xl pl-2 pr-3 py-1.5 max-w-[75%]"
                style={{ backgroundColor: "#FFE89F" }}
              >
                <HiVolumeUp className="shrink-0 text-white" fontSize={24} />
                <span className="text-sm font-bold text-gray-800">{m.content}</span>
              </div>
            </div>
          )
        )}

        {loading && (
          <div className="flex items-start gap-2">
            <div
              className="flex gap-1 bg-white border rounded-xl px-4 py-3"
              style={{ borderColor: "#DDDDDD" }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce" />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}

        {/* Your-turn cue — mirrors mobile's <TypingIndicator isUser /> footer while it waits for you. */}
        {!loading && !ended && !input.trim() && messages.length > 0 && (
          <div className="flex items-start gap-2 justify-end">
            <div className="flex gap-1 rounded-xl px-4 py-3" style={{ backgroundColor: "#FFE89F" }}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#8a6d00]/40 animate-bounce" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#8a6d00]/40 animate-bounce [animation-delay:150ms]" />
              <span className="w-1.5 h-1.5 rounded-full bg-[#8a6d00]/40 animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input, or the end-of-session score card (sample data — the preview has no real scoring) */}
      {ended ? (
        <div className="relative z-10 w-full px-3 pb-1">
          <div className="rounded-2xl bg-white border border-gray-200 px-4 py-3 grid grid-cols-2 gap-x-4 gap-y-2">
            {SAMPLE_SCORES.map(({ label, icon: Icon, value, bg }) => (
              <div key={label} className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0">
                  <Icon className="shrink-0 text-[#2626A5]" fontSize={15} />
                  <span className="text-[13px] font-extrabold text-[#2626A5] truncate">
                    {label}
                  </span>
                </span>
                <span
                  className="shrink-0 w-11 text-center text-[13px] font-extrabold text-white rounded-md px-1.5 py-0.5"
                  style={{ backgroundColor: bg }}
                >
                  {value}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-1 text-center text-[11px] font-medium text-gray-400">
            Conversation complete
          </p>
        </div>
      ) : (
        <div className="relative z-10 w-full px-3 flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type your message…"
            className="flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm outline-none focus:border-blue-400"
          />
          <button
            type="button"
            onClick={send}
            disabled={!input.trim() || loading}
            className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center text-white disabled:opacity-40"
            style={{ background: "linear-gradient(180deg, #FF8500 0%, #FFB800 100%)" }}
          >
            <IoSend className="text-base" />
          </button>
        </div>
      )}

      {/* End-confirm — mirrors mobile EndConversationModal. */}
      {showEndConfirm && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/40 px-6">
          <div className="w-full max-w-[340px] rounded-2xl bg-white p-6 flex flex-col items-center">
            <p className="text-base font-bold text-black text-center">End conversation?</p>
            <p
              className="mt-2 text-[13px] font-medium text-center leading-tight"
              style={{ color: "#727171" }}
            >
              Exit without score or finish the conversation to get your score.
            </p>
            <div className="mt-6 flex gap-2 self-stretch">
              <button
                type="button"
                onClick={() => setShowEndConfirm(false)}
                className="flex-1 h-11 rounded-xl border bg-white text-base font-bold"
                style={{ borderColor: "#C7C7CC", color: "#727171" }}
              >
                Continue
              </button>
              <button
                type="button"
                onClick={() => setShowEndConfirm(false)}
                className="flex-1 h-11 rounded-xl text-base font-bold text-white"
                style={{ backgroundColor: "#3A86FF" }}
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </LiveMobileViewContainer>
  );
}
